import {Component, Inject, OnDestroy} from '@angular/core';
import { BehaviorSubject } from "rxjs";
import {AsyncPipe, NgForOf, NgIf, NgTemplateOutlet} from "@angular/common";
import {AppButtonModule} from "../../../dave-utils-module/app-button-module/app-button.module";
import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef} from "@angular/material/legacy-dialog";
import {
    CreateFolderStructureFolder,
    CreateFolderStructureRequest,
    Folder,
    GatewayRequest,
    GatewayResponse
} from "@dave/types";
import {AppGatewayService} from "../../../dave-data-module/services/app-gateway.service";
import {fileUploadParamsObj} from "../../../dave-data-module/State/actions/files.actions";
import {Action, Store} from "@ngrx/store";
import {State} from "../../../dave-data-module/State";
import {FilesActionTypes} from "../../../dave-data-module/State/actions/files.actions";
import {DaveLoadingModule} from "../../../dave-loading/dave-loading.module";
import {Actions, ofType} from "@ngrx/effects";
import {take} from "rxjs/operators";
import {FolderDataService} from "../../../dave-data-module/services/folder-data.service";

interface FolderStructure {
    name: string;
    children: FolderStructure[];
    file: File|null;
}
export interface FolderUploadComponentDialogData {
    rootFolderId: number | null;
}

@Component({
    selector: 'app-folder-upload',
    templateUrl: './folder-upload.component.html',
    styleUrls: ['./folder-upload.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        AsyncPipe,
        NgForOf,
        NgTemplateOutlet,
        AppButtonModule,
        FontAwesomeModule,
        DaveLoadingModule
    ],
})
export class FolderUploadComponent {
    public loading = new BehaviorSubject(false);
    public loadingText = new BehaviorSubject('');
    public folders = new BehaviorSubject<FolderStructure[]>([]);
    constructor(
        public DialogRef: MatLegacyDialogRef<FolderUploadComponent>,
        @Inject(MAT_DIALOG_DATA)
        public Data: FolderUploadComponentDialogData,
        protected gateway: AppGatewayService,
        protected store: Store<State>,
        protected actions: Actions,
        protected folderDataService: FolderDataService,
    ) {
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
    }

    async onDrop(event: DragEvent): Promise<void> {
        event.preventDefault();
        const items = event.dataTransfer?.items;
        if (!items) return;

        const folderStructure: FolderStructure[] = [];
        // Process all dropped items
        const scanPromises: Promise<void>[] = [];
        for (let i = 0; i < items.length; i++) {
            const entry = items[i].webkitGetAsEntry();
            if (entry) {
                scanPromises.push(new Promise(resolve => {
                    this.traverseEntry(entry).then((res) => {
                        if (res) {
                            folderStructure.push(res);
                        }
                        resolve();
                    });
                }));
            }
        }

        Promise.all(scanPromises).then(() => {
            this.sortFolderStructure(folderStructure);
            this.folders.next(folderStructure);
        })
    }

    private sortFolderStructure(folders: FolderStructure[]): void {
        folders.sort((a, b) => a.name.localeCompare(b.name));
        folders.forEach(folder => {
            if (folder.children && folder.children.length > 0) {
                this.sortFolderStructure(folder.children);
            }
        });
    }

    private traverseEntry(entry: FileSystemEntry): Promise<FolderStructure|null> {
        return new Promise((resolve) => {
            if (entry.isFile) {
                (entry as FileSystemFileEntry).file((file: File) => {
                    resolve({
                        name: entry.name,
                        children: [],
                        file: file,
                    });
                });
            } else if (entry.isDirectory) {
                const dirReader = (entry as FileSystemDirectoryEntry).createReader();

                // readEntries may not return all entries in a single call, so we loop until empty
                this.readAllDirectoryEntries(dirReader).then(async (entries) => {
                    let res: FolderStructure|null = null;
                    if (entries.length === 0) {
                        // Empty folder found!
                        res = {
                            name: entry.name,
                            children: [],
                            file: null,
                        };
                    } else {
                        res = {
                            name: entry.name,
                            children: [],
                            file: null,
                        };
                        // Recursively scan children
                        await Promise.all(entries.map(childEntry =>
                            this.traverseEntry(childEntry).then((child) => {
                                if (child) {
                                    res.children.push(child);
                                }
                            })
                        ));
                    }
                    resolve(res);
                });
            } else {
                resolve(null);
            }
        });
    }

    // Helper method to ensure all entries in a directory are read
    private readAllDirectoryEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
        const entries: FileSystemEntry[] = [];

        return new Promise((resolve) => {
            const read = () => {
                reader.readEntries((batch) => {
                    if (batch.length === 0) {
                        resolve(entries);
                    } else {
                        entries.push(...batch);
                        read(); // Continue reading remaining entries
                    }
                });
            };
            read();
        });
    }

    mapFolderToRequest(i: FolderStructure): CreateFolderStructureFolder {
        return {
            Name: i.name,
            Children: i.children.filter(f => f.file === null).map((child) => this.mapFolderToRequest(child))
        };
    }

    upload(){
        this.loading.next(true);
        this.loadingText.next('Ordner anlegen');
        let req: CreateFolderStructureRequest = {
            ParentFolderId: this.Data.rootFolderId.toString(),
            Folders: this.folders.getValue().map(folder => this.mapFolderToRequest(folder))
        }
        this.gateway.Request({
            FileCreateFolderStructure: req
        }as GatewayRequest).then(res => {
            if (res.FileCreateFolderStructure){
                this.folderDataService.getFolderByParent(this.Data.rootFolderId, false)//TODO insert into cache
                this.folders.getValue().forEach(folder => {
                    let rootFolders = res.FileCreateFolderStructure.Folders.filter(f => f.ParentId === this.Data.rootFolderId.toString());
                    let currentFolder = rootFolders.find(f => f.Name === folder.name);
                    let uploadActions = this.uploadFilesPrepare(
                        folder,
                        res.FileCreateFolderStructure.Folders,
                        res.FileCreateFolderStructure.Folders.filter(f => f.ParentId === currentFolder.Id),
                        currentFolder);
                    if (uploadActions.length === 0) {
                        this.loading.next(false);
                        this.loadingText.next('');
                        this.folders.next([]);
                    } else {
                        this.loadingText.next('Dateien Hochladen 0/'+uploadActions.length);
                        let current = 0;
                        this.store.dispatch(uploadActions[current]);
                        this.actions.pipe(
                            ofType(FilesActionTypes.UploadFileBackendFailure, FilesActionTypes.UploadFileBackendSuccess),
                            take(uploadActions.length)
                        ).subscribe(() => {
                            current++;
                            if (current < uploadActions.length) {
                                this.loadingText.next('Dateien Hochladen '+(current+1)+'/'+uploadActions.length);
                                this.store.dispatch(uploadActions[current]);
                            } else {
                                this.loading.next(false);
                                this.loadingText.next('');
                                this.folders.next([]);
                            }
                        });
                        // this.store.actions$.pipe(ofType(FilesActionTypes.UploadFileBackendFailure, FilesActionTypes.UploadFileBackendSuccess))
                    }

                });
            }
        })
    }

    uploadFilesPrepare(i: FolderStructure, allFolders: Folder[], childrenFolder: Folder[], folder: Folder): Action[] {
        let out: Action[] = [];
        i.children.forEach(child => {
            if (child.file !== null) {
                let param = new Map<keyof fileUploadParamsObj, string>();
                param.set('name', child.file.name);
                param.set('folder_id', folder.Id);
                out.push(FilesActionTypes.UploadFileBackendRequest({ Payload: {
                    file: child.file,
                    params: param,
                }}));
                // this.store.dispatch(FilesActionTypes.UploadFileBackendRequest({ Payload: {
                //     file: child.file,
                //     params: param,
                // }}));
                // firstValueFrom(this.actions$.pipe(ofType(FilesActionTypes.UploadFileBackendFailure, FilesActionTypes.UploadFileBackendSuccess)));
            } else {
                let currentFolder = childrenFolder.find(f => f.Name === child.name);
                let subOut = this.uploadFilesPrepare(child,
                    allFolders,
                    allFolders.filter(f => f.ParentId === currentFolder.Id),
                    currentFolder);
                out.push(...subOut);
            }
        });
        return out;
    }
}
